var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { React } from 'libs/react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { Spinner } from 'components/Spinner';
import './StoreStripeCheckout.scss';
const stripePromises = new Map();
function getStripePromise(publishableKey, stripeAccount) {
    const cacheKey = `${publishableKey}:${stripeAccount}`;
    const cached = stripePromises.get(cacheKey);
    if (cached) {
        return cached;
    }
    const promise = loadStripe(publishableKey, { stripeAccount });
    stripePromises.set(cacheKey, promise);
    return promise;
}
/**
 * @component StoreStripeCheckout
 * @since 1.0.0
 */
export function StoreStripeCheckout(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const { deal, returnPath, checkoutSessionKey, contactValues, onOutOfStock } = props;
    const onOutOfStockRef = useRef(onOutOfStock);
    onOutOfStockRef.current = onOutOfStock;
    const onProductInactiveRef = useRef(props.onProductInactive);
    onProductInactiveRef.current = props.onProductInactive;
    const onPromoCodeInvalidRef = useRef(props.onPromoCodeInvalid);
    onPromoCodeInvalidRef.current = props.onPromoCodeInvalid;
    const onAlreadyPaidRef = useRef(props.onAlreadyPaid);
    onAlreadyPaidRef.current = props.onAlreadyPaid;
    const contactValuesRef = useRef(contactValues);
    contactValuesRef.current = contactValues;
    const [canMountCheckout, setCanMountCheckout] = useState(false);
    const isStripeConfigured = ((_a = deal.paymentIntegration) === null || _a === void 0 ? void 0 : _a.type) === 'stripe' &&
        !!((_b = deal.paymentIntegration) === null || _b === void 0 ? void 0 : _b.accountId);
    const stripePromise = useMemo(() => {
        var _a, _b;
        return getStripePromise(((_a = deal.paymentIntegration) === null || _a === void 0 ? void 0 : _a.key) || '', ((_b = deal.paymentIntegration) === null || _b === void 0 ? void 0 : _b.accountId) || '');
    }, [(_c = deal.paymentIntegration) === null || _c === void 0 ? void 0 : _c.key, (_d = deal.paymentIntegration) === null || _d === void 0 ? void 0 : _d.accountId]);
    const dealPaymentId = (_g = (_f = (_e = deal.payments) === null || _e === void 0 ? void 0 : _e.find((payment) => payment.status === 'ACTIVE' && payment.description === 'Upsell')) === null || _f === void 0 ? void 0 : _f.id) !== null && _g !== void 0 ? _g : (_j = (_h = deal.payments) === null || _h === void 0 ? void 0 : _h.find((payment) => payment.status === 'ACTIVE')) === null || _j === void 0 ? void 0 : _j.id;
    const fetchClientSecret = useCallback(() => {
        const contact = contactValuesRef.current;
        const params = new URLSearchParams({
            id: dealPaymentId !== null && dealPaymentId !== void 0 ? dealPaymentId : '',
            directSale: 'true',
            returnPath
        });
        return fetch(process.env.API_URL_ROOT + '/stripe?' + params.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                firstName: contact === null || contact === void 0 ? void 0 : contact.firstName,
                lastName: contact === null || contact === void 0 ? void 0 : contact.lastName,
                phone: contact === null || contact === void 0 ? void 0 : contact.phone,
                location: contact === null || contact === void 0 ? void 0 : contact.location
            })
        })
            .then((res) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            const data = yield res.json();
            // Le stock est de nouveau vérifié et réservé à l'ouverture du paiement.
            if (res.status === 409 && (data === null || data === void 0 ? void 0 : data.error) === 'OUT_OF_STOCK') {
                (_a = onOutOfStockRef.current) === null || _a === void 0 ? void 0 : _a.call(onOutOfStockRef);
                throw new Error('OUT_OF_STOCK');
            }
            if (res.status === 409 && (data === null || data === void 0 ? void 0 : data.error) === 'PRODUCT_INACTIVE') {
                (_b = onProductInactiveRef.current) === null || _b === void 0 ? void 0 : _b.call(onProductInactiveRef);
                throw new Error('PRODUCT_INACTIVE');
            }
            // Le code promo a pu expirer ou s'épuiser depuis l'approbation.
            if (res.status === 409 && (data === null || data === void 0 ? void 0 : data.error) === 'PROMO_CODE_INVALID') {
                (_c = onPromoCodeInvalidRef.current) === null || _c === void 0 ? void 0 : _c.call(onPromoCodeInvalidRef, (_d = data.reason) !== null && _d !== void 0 ? _d : null);
                throw new Error('PROMO_CODE_INVALID');
            }
            if (data === null || data === void 0 ? void 0 : data.alreadyPaid) {
                (_e = onAlreadyPaidRef.current) === null || _e === void 0 ? void 0 : _e.call(onAlreadyPaidRef);
                return new Promise(() => undefined);
            }
            return data.clientSecret;
        }));
    }, [dealPaymentId, returnPath]);
    const checkoutOptions = useMemo(() => ({ fetchClientSecret }), [fetchClientSecret]);
    useEffect(() => {
        setCanMountCheckout(false);
        const frameId = window.requestAnimationFrame(() => {
            setCanMountCheckout(true);
        });
        return () => {
            window.cancelAnimationFrame(frameId);
            setCanMountCheckout(false);
        };
    }, [checkoutSessionKey]);
    if (!isStripeConfigured) {
        return (React.createElement("p", { className: "store-stripe-checkout__error" }, t('No payment method is configured.')));
    }
    if (!dealPaymentId) {
        return (React.createElement("p", { className: "store-stripe-checkout__error" }, t('Payment is not available for this order.')));
    }
    if (!canMountCheckout) {
        return (React.createElement("div", { className: "store-stripe-checkout store-stripe-checkout--loading" },
            React.createElement(Spinner, null)));
    }
    return (React.createElement("div", { className: "store-stripe-checkout" },
        React.createElement(EmbeddedCheckoutProvider, { key: checkoutSessionKey, stripe: stripePromise, options: checkoutOptions },
            React.createElement(EmbeddedCheckout, null))));
}
